(() => {
  const selectors = {
    item: '.jdgm-carousel-item',
    body: '.jdgm-carousel-item__review-body',
    name: '.jdgm-carousel-item__reviewer-name',
    rating: '.jdgm-carousel-item__review-rating',
    productImage: '.jdgm-carousel-item__product-image',
    productLink: '.jdgm-carousel-item__product a[href]',
  };
  const avatarColors = ['#7f1723', '#886440', '#705246', '#976651'];

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const highResolutionShopifyImage = (url) => {
    if (!url) return '';
    return url.replace(/_\d+x\d+(?=\.(?:avif|gif|jpe?g|png|webp)(?:\?|$))/i, '');
  };

  const readReviews = (source) => {
    const seen = new Set();
    return Array.from(source.querySelectorAll(selectors.item))
      .map((item) => {
        const image = item.querySelector(selectors.productImage);
        const reviewId = item.dataset.reviewId || '';
        const productTitle = image?.getAttribute('alt')?.trim() || '';
        const productImage = highResolutionShopifyImage(
          image?.getAttribute('data-src-retina') || image?.getAttribute('data-src') || image?.getAttribute('src') || ''
        );
        const productUrl = item.querySelector(selectors.productLink)?.getAttribute('href')?.trim() || '';
        const name = item.querySelector(selectors.name)?.textContent.trim() || 'Anonymous';
        const body = item.querySelector(selectors.body)?.textContent.replace(/\s+/g, ' ').trim() || '';
        const ratingNode = item.querySelector(selectors.rating);
        const parsedRating = Number.parseInt(ratingNode?.getAttribute('aria-label') || '', 10);
        const rating = Number.isFinite(parsedRating)
          ? Math.max(1, Math.min(5, parsedRating))
          : Math.max(1, Math.min(5, ratingNode?.querySelectorAll('.jdgm--on').length || 5));
        return { reviewId, productTitle, productImage, productUrl, name, body, rating };
      })
      .filter((review) => {
        const key = review.reviewId || `${review.name}-${review.body}`;
        if (!review.productTitle || !review.productImage || !review.body || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 5);
  };

  const createStars = (rating) => {
    const stars = element('div', 'new-home-testimonials__stars', `${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}`);
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${rating} out of 5 stars`);
    return stars;
  };

  const normalizeProductTitle = (title) => title.trim().toLocaleLowerCase();

  const productHandleFromTitle = (title) => title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const readProductMap = (section) => {
    const source = section.querySelector('[data-testimonials-product-map]');
    if (!source) return {};
    try {
      return JSON.parse(source.textContent);
    } catch (error) {
      console.warn('Customer Love product map could not be parsed.', error);
      return {};
    }
  };

  const withReviewsAnchor = (url) => {
    if (!url) return '';
    try {
      const destination = new URL(url, window.location.origin);
      destination.hash = 'judgeme_product_reviews';
      return destination.origin === window.location.origin
        ? `${destination.pathname}${destination.search}${destination.hash}`
        : destination.toString();
    } catch (error) {
      return '';
    }
  };

  const configureReviewButton = (section, featuredReview) => {
    const button = section.querySelector('[data-testimonials-button]');
    if (!button) return;

    const customUrl = button.dataset.customUrl?.trim();
    if (customUrl) {
      button.removeAttribute('data-automatic-review-link');
      button.removeAttribute('aria-disabled');
      return;
    }

    const productMap = readProductMap(section);
    const mappedUrl = productMap[normalizeProductTitle(featuredReview.productTitle)];
    const fallbackHandle = productHandleFromTitle(featuredReview.productTitle);
    const productUrl = featuredReview.productUrl || mappedUrl || (fallbackHandle ? `/products/${fallbackHandle}` : '');
    const reviewUrl = withReviewsAnchor(productUrl);

    if (reviewUrl) {
      button.href = reviewUrl;
      button.dataset.automaticReviewLink = 'true';
      button.removeAttribute('aria-disabled');
    }
  };

  const enableSmoothReviewNavigation = (section) => {
    if (section.dataset.reviewNavigationInitialized === 'true') return;
    section.dataset.reviewNavigationInitialized = 'true';

    section.addEventListener('click', (event) => {
      const button = event.target.closest('[data-testimonials-button][data-automatic-review-link="true"]');
      if (
        !button
        || button.getAttribute('aria-disabled') === 'true'
        || event.defaultPrevented
        || event.button !== 0
        || event.metaKey
        || event.ctrlKey
        || event.shiftKey
        || event.altKey
      ) return;

      const destination = new URL(button.href, window.location.origin);
      if (destination.origin !== window.location.origin) return;

      event.preventDefault();
      try {
        sessionStorage.setItem('finewine:smooth-scroll-customer-reviews', 'true');
      } catch (error) {
        window.location.assign(button.href);
        return;
      }
      destination.hash = '';
      window.location.assign(destination.toString());
    });
  };

  const createFeatured = (review) => {
    const article = element('article', 'new-home-testimonials__featured');
    article.setAttribute('aria-label', `${review.productTitle} review by ${review.name}`);
    const image = element('img', 'new-home-testimonials__featured-image');
    image.src = review.productImage;
    image.alt = review.productTitle;
    image.loading = 'lazy';
    image.decoding = 'async';
    const copy = element('div', 'new-home-testimonials__featured-copy');
    copy.append(
      element('p', 'new-home-testimonials__featured-quote', review.body),
      createStars(review.rating),
      element('p', 'new-home-testimonials__author', review.name)
    );
    article.append(image, copy);
    return article;
  };

  const createCard = (review, index) => {
    const article = element('article', 'new-home-testimonials__card');
    article.setAttribute('aria-label', `${review.productTitle} review by ${review.name}`);
    const avatar = element('span', 'new-home-testimonials__avatar', review.name.charAt(0) || 'A');
    avatar.setAttribute('aria-hidden', 'true');
    avatar.style.setProperty('--nht-avatar-background', avatarColors[index % avatarColors.length]);
    const copy = element('div', 'new-home-testimonials__card-copy');
    copy.append(
      element('h3', 'new-home-testimonials__product-title', review.productTitle),
      element('p', 'new-home-testimonials__body', review.body),
      createStars(review.rating),
      element('p', 'new-home-testimonials__author', review.name)
    );
    article.append(avatar, copy);
    return article;
  };

  const setupSlider = (cards, cardsWrapper, cardReviews) => {
    if (cardReviews.length <= 1) return;

    const dotsContainer = element('div', 'new-home-testimonials__dots');
    dotsContainer.setAttribute('role', 'tablist');
    dotsContainer.setAttribute('aria-label', 'Customer reviews pagination');

    const dots = cardReviews.map((_, index) => {
      const dot = element('button', `new-home-testimonials__dot${index === 0 ? ' is-active' : ''}`);
      dot.setAttribute('type', 'button');
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      dot.setAttribute('aria-label', `Review ${index + 1} of ${cardReviews.length}`);
      dot.addEventListener('click', () => {
        const targetCard = cards.children[index];
        if (targetCard) {
          const cardLeft = targetCard.offsetLeft - cards.offsetLeft;
          cards.scrollTo({ left: cardLeft, behavior: 'smooth' });
        }
      });
      dotsContainer.append(dot);
      return dot;
    });

    let ticking = false;
    const updateActiveDot = () => {
      const scrollLeft = cards.scrollLeft;
      let closestIndex = 0;
      let minDistance = Infinity;

      Array.from(cards.children).forEach((card, idx) => {
        const cardLeft = card.offsetLeft - cards.offsetLeft;
        const distance = Math.abs(cardLeft - scrollLeft);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = idx;
        }
      });

      dots.forEach((dot, idx) => {
        const isActive = idx === closestIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      ticking = false;
    };

    cards.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(updateActiveDot);
        ticking = true;
      }
    }, { passive: true });

    let isDown = false;
    let startX = 0;
    let scrollStart = 0;
    let hasDragged = false;

    cards.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      isDown = true;
      hasDragged = false;
      startX = e.pageX - cards.offsetLeft;
      scrollStart = cards.scrollLeft;
      cards.style.scrollSnapType = 'none';
      cards.style.scrollBehavior = 'auto';
    });

    window.addEventListener('mouseup', () => {
      if (!isDown) return;
      isDown = false;
      cards.style.scrollSnapType = '';
      cards.style.scrollBehavior = '';
      setTimeout(() => {
        hasDragged = false;
      }, 50);
    });

    cards.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      const x = e.pageX - cards.offsetLeft;
      const walk = x - startX;
      if (Math.abs(walk) > 4) {
        hasDragged = true;
      }
      cards.scrollLeft = scrollStart - walk;
    });

    cards.addEventListener('click', (e) => {
      if (hasDragged) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    cardsWrapper.append(dotsContainer);
  };

  const render = (section) => {
    const source = section.querySelector('[data-testimonials-source]');
    const output = section.querySelector('[data-testimonials-output]');
    if (!source || !output || output.dataset.rendered === 'true') return;
    const reviews = readReviews(source);
    if (!reviews.length) {
      if (window.Shopify?.designMode) {
        output.append(element('p', 'new-home-testimonials__empty', 'Feature product reviews in Judge.me to populate Customer Love.'));
      } else {
        section.hidden = true;
      }
      return;
    }
    const cardsWrapper = element('div', 'new-home-testimonials__cards-wrapper');
    const cards = element('div', 'new-home-testimonials__cards');
    cards.setAttribute('role', 'region');
    cards.setAttribute('aria-label', 'Customer reviews');
    const cardReviews = reviews.slice(1);
    cardReviews.forEach((review, index) => cards.append(createCard(review, index)));
    cardsWrapper.append(cards);
    setupSlider(cards, cardsWrapper, cardReviews);

    output.append(createFeatured(reviews[0]), cardsWrapper);
    configureReviewButton(section, reviews[0]);
    output.dataset.rendered = 'true';
  };

  const init = (scope = document) => {
    if (scope.matches?.('[data-new-home-testimonials]')) {
      enableSmoothReviewNavigation(scope);
      render(scope);
    }
    scope.querySelectorAll('[data-new-home-testimonials]').forEach((section) => {
      enableSmoothReviewNavigation(section);
      render(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
